/**
 * One company in the follow-up queue.
 *
 * The domain opens the brain OVER this page rather than navigating to the
 * company. Two reasons, and only the second one is about convenience: this
 * page's filter and its scroll position are component state, so leaving throws
 * away the list somebody just built - and a row with no register code has no
 * company page to leave TO. The brain is keyed by domain precisely so those
 * rows are reachable, and `BrainDialog` is the same card the register detail
 * page stacks, so there is no second copy to keep in step.
 *
 * The register link is therefore an EXTRA, drawn only when `regCode` is there.
 * It is not the row's main action and must never be the only way in.
 *
 * THE QUICK ACTIONS ARE THE POINT OF THE PAGE. A queue you can only read is a
 * queue that goes stale: the answer to most overdue rows is "not yet, give it
 * another week", and if that costs opening a card and finding a date field,
 * nobody does it and the whole list rots into noise.
 */
import { Building2, CalendarClock, Loader2, Phone, Wrench, X } from "lucide-react";
import { cn } from "@wa/ui";
import { Link } from "react-router-dom";
import type { BrainDueRow } from "../../../lib/siteBrain";
import { VerdictDriftMark } from "../ariregister/brain";
import { IconBtn, StatusPill, fmtDate, fmtStamp, type StatusTone } from "../../ui";
import type { T } from "../site-scanner/types";

/**
 * The verdict's tone.
 *
 * "no" stays muted rather than turning red: the brain deciding a company is not
 * worth selling to is an answer, not a problem, and colouring it as a failure
 * would make a healthy queue look alarming.
 */
const FIT_TONE: Record<"go" | "hold" | "no", StatusTone> = { go: "ok", hold: "wt", no: "mut" };

/** Outcome and date together, for the cell that can only show one of them. */
function outcomeTitle(row: BrainDueRow, t: T): string {
  const when = fmtStamp(row.calledAt);
  if (!row.callOutcome) return when;
  return `${t(`brain.script.outcomeLabel.${row.callOutcome}`)} - ${when}`;
}

export function BrainDueRowItem({
  row,
  cols,
  canManage,
  busy,
  onOpen,
  onPostpone,
  onClear,
  t,
}: {
  row: BrainDueRow;
  cols: string;
  canManage: boolean;
  busy: boolean;
  onOpen: () => void;
  onPostpone: (days: number) => void;
  onClear: () => void;
  t: T;
}) {
  // The strongest triage signal after the date: a company that has repaired
  // something since we last looked is a company that reads its post.
  const repaired = row.repaired && row.repaired.fixed > 0 ? row.repaired : null;

  return (
    <div className="crm-list-row relative" style={{ gridTemplateColumns: cols }}>
      <span className="min-w-0 pl-3">
        <span className="flex min-w-0 items-center gap-2">
          {/* A button, not a Link: this opens a dialog. The stretched overlay
              makes the whole row the target, the same way the campaign list
              stretches its name link. */}
          <button
            type="button"
            onClick={onOpen}
            className="crm-prow-n crm-trunc min-w-0 text-left after:absolute after:inset-0 after:rounded-full"
          >
            {row.domain}
          </button>
          {/* The pill shares the squeeze instead of winning it. Without
              `crm-trunc min-w-0` the pill keeps its full width and the DOMAIN
              absorbs every lost pixel, so at the card's scroll floor
              "sel-nadalal.ee" rendered as "s." beside a perfectly legible
              verdict. The name is the row's identity; the verdict is a note
              about it. */}
          {row.fit ? (
            <StatusPill tone={FIT_TONE[row.fit]} className="crm-trunc min-w-0">
              {t(`brain.fit.decision.${row.fit}`)}
            </StatusPill>
          ) : null}
          {/* Beside the pill, because it is a statement about the pill. This
              queue is where "who do I ring this morning" is actually decided,
              so a verdict that improved on its own is worth the same 14px here
              as it is on the brain's own list. */}
          <VerdictDriftMark drift={row.repaired?.drift} t={t} />
        </span>
        {/* `title` on both truncating cells. The note is a whole sentence
            somebody typed after a call ("waiting for their new manager"), and
            an ellipsis with no way to read the rest is the same as not having
            stored it. */}
        {row.note || repaired ? (
          <span className="crm-prow-s flex min-w-0 items-center gap-1.5">
            {/* ⚠ ONLY WHEN SOMETHING WAS REPAIRED. A "0 fixed" mark would be the
                negative half of this sentence, which is both an accusation and
                the half the comparison is least able to prove - the same rule the
                call script's own progress box lives by. The count of what got
                worse rides in the tooltip, where it informs without shouting. */}
            {repaired ? (
              <span
                className="shrink-0 font-semibold"
                style={{ color: "var(--color-success)" }}
                title={t("brainDue.repairedAt", { date: fmtDate(repaired.at), appeared: repaired.appeared })}
              >
                <Wrench className="mr-1 inline size-3" />
                {t("brainDue.repaired", { n: repaired.fixed })}
              </span>
            ) : null}
            {row.note ? (
              <span className="crm-trunc" title={row.note}>
                {row.note}
              </span>
            ) : null}
          </span>
        ) : null}
      </span>

      {/* THE DATE IS THE COLUMN, THE LATENESS IS A MARK BESIDE IT.
          The first version put the whole sentence here ("Late by 12 days (since
          2026-08-05)") and it measured 238px against a 170px track on a normal
          CRM window - so the one number that says how badly this is overdue was
          the one thing clipped away. The bucket heading already says these rows
          are late; the row only has to say the date and by how much. */}
      <span className="flex min-w-0 items-center gap-1.5">
        <CalendarClock className={cn("size-3.5 shrink-0", row.due ? "text-[var(--color-warning)]" : "crm-mut")} />
        <span className="crm-tnum crm-trunc text-[12.5px]">{fmtDate(row.nextTouchAt)}</span>
        {row.due ? (
          <span
            className="crm-tnum shrink-0 text-[12px] font-bold text-[var(--color-warning)]"
            title={t("brainDue.late", { n: Math.abs(row.inDays) })}
          >
            {t("brainDue.lateDays", { n: Math.abs(row.inDays) })}
          </span>
        ) : null}
      </span>

      {/* What happened last time somebody rang. An empty cell means nobody has,
          which is a different thing from "rang and got nowhere" and the two
          must not look alike. */}
      <span className="crm-mut flex min-w-0 items-center gap-1.5 text-[12.5px]">
        {row.calledAt ? (
          <>
            <Phone className="size-3.5 shrink-0" />
            {/* The outcome answers "what happened", the timestamp answers
                "when" - and only one of them fits, so the other rides in the
                tooltip rather than being dropped. */}
            <span className="crm-trunc" title={outcomeTitle(row, t)}>
              {row.callOutcome ? t(`brain.script.outcomeLabel.${row.callOutcome}`) : fmtStamp(row.calledAt)}
            </span>
          </>
        ) : null}
      </span>

      <span className="flex items-center justify-end gap-1 pr-2">
        {/* Above the row's stretched overlay, or the overlay eats the clicks. */}
        {row.regCode ? (
          <Link
            to={`/ariregister/${row.regCode}`}
            className="crm-cb crm-cb-sm relative z-[1]"
            title={t("brainDue.openCompany")}
            aria-label={t("brainDue.openCompany")}
          >
            <Building2 className="size-3.5" />
          </Link>
        ) : null}

        {canManage ? (
          <>
            <IconBtn
              small
              wide
              disabled={busy}
              onClick={() => onPostpone(7)}
              title={t("brain.plan.in.week")}
              className="relative z-[1]"
            >
              {busy ? <Loader2 className="size-3.5 animate-spin" /> : t("brainDue.plus7")}
            </IconBtn>
            <IconBtn
              small
              wide
              disabled={busy}
              onClick={() => onPostpone(30)}
              title={t("brain.plan.in.month")}
              className="relative z-[1]"
            >
              {t("brainDue.plus30")}
            </IconBtn>
            <IconBtn
              small
              disabled={busy}
              onClick={onClear}
              title={t("brain.plan.clear")}
              aria-label={t("brain.plan.clear")}
              className="relative z-[1]"
            >
              <X className="size-3.5" />
            </IconBtn>
          </>
        ) : null}
      </span>
    </div>
  );
}
